import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ColDef, ColumnResizedEvent, ColumnVisibleEvent, GridApi, GridOptions, GridReadyEvent, GridSizeChangedEvent } from 'ag-grid-community';
import { ToggleButtonObjectType } from '@niceltd/cxone-components/toggle-button-group/src/lib/toggle-button-group.component';
import moment from 'moment';
import { NavigationService } from 'src/app/navigation/navigation.service';
import { AgContextMenuService } from 'src/app/reusables/services/ag-context-menu.service';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleManager } from 'src/shared/models/module-manager';
import { GroupSelectorDTO } from 'src/AngularJS/Reusables/group-selector.models';
import { ModuleManagerRefreshType } from 'src/shared/models/module-manager/module-manager-options.model';
import SlideoutSettingsManager from 'src/shared/models/slideout-settings-manager';
import { AgentStateDetailComponent } from 'src/app/dashboard/modals/agent-state-detail/agent-state-detail.component';
import { TasksModalComponent } from 'src/app/dashboard/modals/tasks-modal/tasks-modal.component';
import { CxonePopoutComponent } from '../../toolbar-items/cxone-popout/cxone-popout.component';
import { CxoneTeamsComponent } from '../../toolbar-items/cxone-teams/cxone-teams.component';
import { CurrentAgentStatesService } from 'src/app/dashboard/modules/current-agent-states/current-agent-states.service';
import { ErrorCode } from 'src/app/dashboard/modules/extended-kpi/extended-kpi.model';
import { QualityScoreMetricCellRendererComponent } from './quality-score-metric-cell-renderer/quality-score-metric-cell-renderer.component';
import { QualityScoreUserCellRendererComponent } from './quality-score-user-cell-renderer/quality-score-user-cell-renderer.component';
import { ModuleView, QualityScoreData, QualityScoreRow } from './quality-score.model';
import { QualityScoreService } from './quality-score.service';
import { AgGridAngular } from 'ag-grid-angular';
import { QualityScoreGridCustomHeaderComponent } from './quality-score-grid-custom-header/quality-score-grid-custom-header.component';
import evaluationsData from './quality-score-evaluations-data.json';
import { IModuleManagerMenuItem, ModuleManagerMenuItemType, ModuleManagerSubMenu } from 'src/shared/models/module-manager/module-manager-menu-item.model';
import { SuggestCoachingModalComponent } from '../../../../dashboard/modals/suggest-coaching-modal/suggest-coaching-modal.component';

@Component({
    selector: 'cv-quality-score',
    templateUrl: './quality-score.component.html',
    styleUrls: ['./quality-score.component.scss']
})
export class QualityScoreComponent implements OnInit {
    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;
    @Input() settingsManager: SlideoutSettingsManager;
    @ViewChild('qualityScoreGrid') agGrid: AgGridAngular;

    modalRef: NgbModalRef = null;

    public isDataLoading = false;
    public data: QualityScoreData = null;
    public avgColor: string = null;
    public filterValue = "";
    public showFilter = false;
    public isCustomView = false;
    public topScores: QualityScoreRow[] = [];
    public bottomScores: QualityScoreRow[] = [];
    public localizationReady: boolean = false;
    public somethingWentWrong = false;
    public dataReady: boolean = false;
    public selectorReady: boolean = false;
    public noData: boolean = true;
    public selectedAgentsDTO: GroupSelectorDTO = null;
    public empSelectorDTO;
    public gridOptions: GridOptions = null;
    public viewOptions: ToggleButtonObjectType[] = [{ id: 'TopBottom', name: 'Top/Bottom' }, { id: 'All', name: 'All' }];
    public popoutContextUrl = "";
    public popoutContext = {
        url: () => this.popoutContextUrl,
        isVisible: () => true // currently always visible
    };
    public inactiveUserMessage = '';
    public context: any;
    public displayView = ModuleView;
    public evaluationsGridOptions: GridOptions;
    public gridApi: GridApi;
    public gridReady = false;
    public tableState = null;
    public stateRestoreInProgress = false;
    public stateSave: number;
    public params: any;
    public updateData = null;

    private gridState: any = null;
    private isFirstLoad = true;
    private stateSaveTimer: number;
    private setupGridTimer: number;
    private topBottomText: string;
    private customText: string;
    public viewBy: string;
    private get isResizeWaiting() { return this.moduleManager?.container.isResizeWaiting; }

    moduleState = {
        name: 'qualityScoreState',
        autoLoad: true,
        initialLoad: false,
        view: ModuleView.Scoreboard
    };

    constructor(
        private agGridHelperService: AgGridHelperService,
        private cvLocalizationService: CVLocalizationService,
        private service: QualityScoreService,
        private configService: CvConfigurationService,
        private navigationService: NavigationService,
        private ngbModal: NgbModal,
        private agentStateService: CurrentAgentStatesService,
        private agContextMenuService: AgContextMenuService,
    ) {

        this.context = { componentParent: this };
    }

    async ngOnInit() {
        const localizationPromises = [
            this.cvLocalizationService.GetSection('Quality_Score_Module'),
            this.cvLocalizationService.GetSection('CXOne_Inactive_User'),
        ];
        await Promise.all(localizationPromises);
        this.moduleManager.setOptions({
            enableWidthClass: false,
            widgetMode: false,
            onSettingsApplied: async settings => {
                this.selectedGroupsChanged(settings.empSelectorDTO);
                await this.loadData(false);
            },
            hasRefresh: true,
            refreshType: ModuleManagerRefreshType.FifteenMinute,
            onRefresh: async isAutoRefresh => {
                this.moduleManager.showLoader = !isAutoRefresh;
                await this.loadData(isAutoRefresh);
            },
            canRefresh: () => !this.isDataLoading,
            onExport: (format, isCXOne) => this.service.GetQualityScoreDataForExport(this.moduleKey, format, isCXOne),
        });
        this.setupGrid();

        if (!this.moduleManager.hasWriteAccess() && !this.configService.isCxOne) {
            this.agGridHelperService.setGridReadOnly(this.gridOptions);
        }

        this.viewBy = this.moduleManager?.customSettings?.SelectedView?.getValue() || this.moduleManager?.customSettings?.SelectedView?.defaultValue;
        this.moduleManager.addState({
            name: "tableState",
            getStateData: () => this.gridState,
            onStateLoaded: stateData => {
                if (!stateData) { // No table state loaded
                    return;
                }
                this.gridState = stateData;
                if (this.agGrid?.api)
                    this.agGridHelperService.loadGridState(this.agGrid?.api, stateData);
            },
            autoLoad: true
        });

        this.moduleManager.addState({
            name: 'qualityScoreState',
            autoLoad: true,
            getStateData: () => this.moduleState,
            onStateLoaded: stateData => {                
                if (!stateData) {
                    this.setModuleState(ModuleView.Scoreboard);
                }
                if (stateData) {
                    this.handleOnStateLoaded(stateData);
                }
            }
        });

        this.moduleManager.addState({
            name: "tableState",
            getStateData: () => this.tableState,
            onStateLoaded: (stateData) => {
                if (!stateData) {
                    return;
                }

                this.tableState = stateData;
                this.restoreColumnState();
            },
            autoLoad: true
        });

        this.moduleManager.addToolbarItem(CxoneTeamsComponent);


        this.topBottomText = this.getLocaleText('Top_Bottom', 'Top/Bottom');
        this.customText = this.getLocaleText('All', 'All');
        this.viewOptions = [{ id: 'TopBottom', name: this.topBottomText }, { id: 'All', name: this.customText }];
        this.inactiveUserMessage = this.getLocaleText('Inactive', 'Inactive');
        this.localizationReady = true;

        this.popoutContextUrl =
            `${this.navigationService.cxoneTokenUrls?.niceBaseUrl ? this.navigationService.cxoneTokenUrls.niceBaseUrl : ''}` +
            `/qm/monitoring/#/performanceMonitoring`;
        this.moduleManager.addToolbarItem(CxonePopoutComponent, this.popoutContext);

        this.evaluationsGrid();
    }

    handleOnStateLoaded(stateData) {
        this.moduleState = stateData;
        if (this.moduleState.view) {
            this.setModuleState(this.moduleState.view);
        } else {
            this.setModuleState(ModuleView.Evaluations);
        }

        if (this.moduleState.view === this.displayView.Evaluations || this.moduleState.view === this.displayView.Channels) {
            this.menuItemsForTable();
        }
        this.moduleState.initialLoad = true;
    }

    setModuleState(state) {
        this.service.setState(this.moduleKey, state === ModuleView.Scoreboard);
        if (this.moduleState.view === state) {
            return;
        }
        this.moduleManager.resetMenu();
        this.moduleState.view = state;

        if (this.gridApi) {
            this.updateData = this.moduleState.view === this.displayView.Channels ? this.updateGridForChannels() : this.updateGridForEvaluations();
            this.gridApi.setGridOption('rowData', this.updateData);
        }

        switch (this.moduleState.view) {
            case (ModuleView.Scoreboard):
                this.moduleState.view = ModuleView.Scoreboard;
                break;
            case (ModuleView.Evaluations):
                this.moduleState.view = ModuleView.Evaluations;
                this.menuItemsForTable();
                break;
            case (ModuleView.Channels):
                this.moduleState.view = ModuleView.Channels;
                this.menuItemsForTable();
                break;
            default:
                this.moduleState.view = ModuleView.Scoreboard;
                break;
        }

        this.moduleManager.saveState('qualityScoreState');
        this.moduleManager.requestPublishNeeded();
    }

    menuItemsForTable() {
        const columnDefs = this.moduleState.view === this.displayView.Channels ? this.channelsColumnDefs : this.evaluationsColumnDefs;

        this.moduleManager.addMenuItem({
            name: this.getLocaleText('Columns'),
            icon: "columns",
            getEnabled: () => this.gridApi && columnDefs.length > 0 && !this.noData,
            type: ModuleManagerMenuItemType.CheckMenu,
            getCheckItems: () => columnDefs,
            getItemName: i => {
                const colDef = columnDefs[i] as ColDef;
                return colDef ? colDef.headerName || colDef.field || "" : "";
            },
            isItemChecked: i => {
                if (this.gridReady && !this.tableState) {
                    this.prepareTableState();
                }

                const columnDefinition: ColDef = columnDefs[i];
                const tableStateMatch = this.tableState.columns.find(c => c.name === columnDefinition.field);
                if (tableStateMatch) {
                    columnDefinition.hide = !tableStateMatch.visible;
                }
                return !columnDefinition.hide;
            },
            onCheckClicked: i => {
                this.prepareTableState();
                this.toggleColumnVisible(i);
            },
        } as IModuleManagerMenuItem);
    }

    prepareTableState() {
        if (!this.gridApi)
            return;

        const tableState = this.agGridHelperService.getGridState(this.gridApi);
        if (tableState) {
            this.tableState = tableState;
        }
    }

    toggleColumnVisible(i: number) {
        if (!this.gridApi) return;

        const columnDefs = this.moduleState.view === this.displayView.Channels ? this.channelsColumnDefs : this.evaluationsColumnDefs;
        const column = columnDefs[i] as ColDef;
        const tableStateMatch = this.tableState.columns.find(c => c.name === column.field);

        if (tableStateMatch) {
            const newVisibility = !tableStateMatch.visible;
            column.hide = !newVisibility;
            this.gridApi.setColumnVisible(column.field, newVisibility);
            this.prepareTableState();
            this.tableStateChanged();
            this.tableStateSave();
        }
    }

    tableStateChanged() {
        this.tableState = this.agGridHelperService.getGridState(this.gridApi);
        this.tableStateSave();
    }

    setTooltipData(rowData) {
        let data = [];
        rowData.forEach(row => {
            data.push(this.viewBy === 'Teams' ? {
                ...row,
                isTeam: true
            } : {
                ...row,
                isTeam: false
            });
        });
        return data;
    }

    private async loadData(isAutoRefresh: boolean) {
        this.dataReady = false;
        if (this.isDataLoading)
            return;
        this.isDataLoading = true;
        this.somethingWentWrong = false;

        if (!isAutoRefresh)
            this.moduleManager.showLoader = true;

        try {
            this.data = await this.service.LoadQualityScoreData(this.moduleKey, isAutoRefresh, this.isFirstLoad);

            if (this.data && this.data.RowData) {
               this.data.RowData = this.setTooltipData(this.data.RowData);                
            }

            if (this.agGrid?.api) {
                this.queueUpdateGrid();
            }
            this.moduleManager.setAutoRefreshReady();
            this.getScoresForAutoView();
            await this.moduleManager.loadSettings(false);

        } catch (err) {
            this.moduleManager.handleLoadError(err, isAutoRefresh);
            this.somethingWentWrong = true;
        } finally {
            this.moduleManager.showLoader = false;
            this.isDataLoading = false;
            this.noData = !(this.data?.RowData?.length > 0);

            if (!this.noData) {
                const fromDate = moment(this.moduleManager.moduleSettings?.selectedDateRange?.startDate).valueOf();
                const toDate = moment(this.moduleManager.moduleSettings?.selectedDateRange?.endDate).valueOf();

                let teams = this.data.RowData.map(r => r.Group.teamUUID).join(',').toLowerCase();
                if (teams.length > 0)
                    teams = "&teams=" + teams;

                let groups = this.data.GroupGUIDs?.join(',').toLowerCase() ?? "";
                if (groups.length > 0)
                    groups = "&groups=" + groups;

                this.popoutContextUrl =
                `${this.navigationService.cxoneTokenUrls?.niceBaseUrl ? this.navigationService.cxoneTokenUrls.niceBaseUrl : ''}` +
                `/qm/monitoring/#/performanceMonitoring?fromDate=${fromDate}&toDate=${toDate}${teams}${groups}`;
            }
        }
    }

    private queueUpdateGrid() {
        if (!this.agGrid?.api)
            return;

        // Cancel previous save attempt, since it hasn't been 500ms, yet.
        if (this.setupGridTimer) {
            clearTimeout(this.setupGridTimer);
            this.setupGridTimer = null;
        }

        // Create a timer to save the state 500ms from now.
        this.setupGridTimer = window.setTimeout(() => {
            this.setupGridTimer = null;
            this.updateColumnDefs();
            this.updateRowData();
        }, 500/*ms*/);
    }

    private updateRowData() {
        if (!this.agGrid?.api)
            return;
        
        this.agGrid?.api?.setGridOption('rowData', []);
        this.agGrid?.api?.setGridOption('rowData', this.data.RowData);
    }

    private updateColumnDefs() {
        if (!this.agGrid?.api)
            return;

        let newColumnDefs = [];
        if (this.data.RowData.length != 0) {
            newColumnDefs.push({
                field: 'Group.groupName',
                headerName: this.cvLocalizationService.getCachedText('Quality_Score_Module', 'Quality_Score'),
                colId: 'Name',
                filter: 'agTextColumnFilter',
                resizable: true,
                cellStyle: { fontSize: '12px' },
                cellRenderer: 'QualityScoreUserCellRenderer',
            });
            newColumnDefs.push({
                field: 'MetricData',
                headerName: this.data.MetricName,
                maxWidth: 48,
                type: 'metricColumn',
                cellRenderer: 'QualityScoreMetricCellRenderer',
                cellRendererParams: {
                    showTotals: true,
                },
                cellStyle: {
                    'display': "flex",
                    'justify-content': "flex-end"
                },
                resizable: true, suppressMovable: true,
                suppressMenu: true,
            });

            this.agGrid?.api.setGridOption('columnDefs', []);
            this.agGrid?.api.setGridOption('columnDefs', newColumnDefs);
            this.agGrid?.api.sizeColumnsToFit();
            this.dataReady = true;
        }
    }

    public evaluationsGrid() {
        const isChannelsView = this.moduleState.view === this.displayView.Channels;

        this.evaluationsGridOptions = this.agGridHelperService.createGridOptions({
            defaultColDef: {
                sortable: true,
                filter: true,
                lockPinned: false,
                resizable: true,
                suppressMenu: false,
                enableValue: true,
            },
            context: { moduleManager: this.moduleManager },
            accentedSort: true,
            animateRows: true,
            suppressCellFocus: true,
            suppressScrollOnNewData: true,
            suppressContextMenu: false,
            onGridReady: this.onGridReady.bind(this),
            onColumnVisible: this.onColumnVisible.bind(this),
            onFilterChanged: () => {
                this.updateTableState();
            },
            onSortChanged: () => this.updateTableState(),
            onColumnMoved: () => this.updateTableState(),
            onColumnPinned: () => this.updateTableState(),
            onColumnResized: this.onColumnResized.bind(this),
            onCellClicked: (params: any) => this.params = params,
            onGridSizeChanged: this.onGridSizeChanged.bind(this),
            treeData: true,
            groupDefaultExpanded: 0,
            getDataPath: (data) => {
                return data.Agent ? [data.Team, data.Agent] : [data.Team];
            },
            autoGroupColumnDef: {
                headerComponent: QualityScoreGridCustomHeaderComponent,
                headerName: this.cvLocalizationService.getCachedText('Quality_Score_Module', 'Team_Agent', 'Team/Agent'),
                cellRendererParams: {
                    suppressCount: true,
                    innerRenderer: (params) => {
                        if (params.node.group) {
                            return params.node.key;
                        } else {
                            return params.data.Agent;
                        }
                    }
                },
                width: 169,
                minWidth: 169,
                cellStyle: { borderRight: "1px solid #EBEBEB" },
                cellRenderer: 'agGroupCellRenderer',
            },
            columnDefs: isChannelsView ? this.channelsColumnDefs : this.evaluationsColumnDefs,
            icons: {
                groupCollapsed:
                    `<div class="sortIcons">
                        <svg class="icon-10">
                            <use href="../App_Themes/CXone/icons/arrow-right.svg#arrow-right"></use>
                        </svg>
                    </div>`,
                groupExpanded:
                    `<div class="sortIcons">
                        <svg class="icon-10">
                            <use href="../App_Themes/CXone/icons/arrow-down.svg#arrow-down"></use>
                        </svg>
                    </div>`
            },
            overlayNoRowsTemplate: `<div class="error-container" style="padding-top: 48px;">
                  <svg class="mb-2">
                      <use href="../App_Themes/CXone/icons/no-results.svg#no-results"></use>
                  </svg>
                  <p class="cxone-error-text">
                      ${this.cvLocalizationService.getCachedText("Error_Module", "CXOne_No_Results", "No results found")}
                  </p>
              </div>`,
            suppressDragLeaveHidesColumns: true,
            onFirstDataRendered: (params) => {
                params.api.forEachNode((node) => {
                    if (node.group && node.expanded) {
                        node.setExpanded(false);
                    }
                });
            }
        },
        { enableCopy: true });
    }

    refreshHeaderComponent(api: GridApi) {
        api.refreshHeader();
    }

    evaluationsColumnDefs = [
        {
            field: 'Evaluations',
            headerName: this.cvLocalizationService.getCachedText('Quality_Score_Module', 'Evaluations', 'Evaluations'),
            filter: 'agTextColumnFilter',
            aggFunc: 'sum',
            cellStyle: {
                textAlign: 'right',
                paddingRight: '22px'
            },
            width: 133,
            minWidth: 133
        },
        {
            field: 'AvgScore',
            headerName: this.cvLocalizationService.getCachedText('Quality_Score_Module', 'AvgScore', 'Avg. Score'),
            aggFunc: (params) => {
                const valuesArray = params.values as number[];
                const validValues = valuesArray.filter(value => !isNaN(value));
                return validValues.length > 0
                    ? (validValues.reduce((sum, val) => sum + val, 0) / validValues.length).toFixed(2)
                    : 0;
            },
            cellStyle: {
                textAlign: 'right',
                paddingRight: '22px'
            },
            width: 133,
            minWidth: 133
        },
        {
            field: 'Variance',
            headerName: this.cvLocalizationService.getCachedText('Quality_Score_Module', 'Variance', 'Variance'),
            cellRenderer: (params) => {
                const variance = params.value;
                if (variance !== null && variance !== undefined) {
                    const absoluteVariance = Math.abs(variance);
                    const arrowIcon = variance > 0 ?
                        `<div class="icons">
                            <svg class="icon-up">
                                <use href="../App_Themes/CXone/icons/cxone-up-arrow.svg#cxone-up-arrow"></use>
                            </svg>
                        </div>` :
                        `<div class="icons">
                            <svg class="icon-down">
                                <use href="../App_Themes/CXone/icons/cxone-down-arrow.svg#cxone-down-arrow"></use>
                            </svg>
                        </div>`;

                    return `<span style="color: black; font-weight: normal; display: flex; align-items: center;">
                                ${arrowIcon} ${absoluteVariance.toFixed(2)}
                            </span>`;
                }
                return '';
            },
            cellStyle: {
                display: 'flex',
                justifyContent: 'flex-end',
                paddingRight: '24px'
            },
            width: 133,
            minWidth: 133
        }
    ];

    channelsColumnDefs = [
        {
            field: 'Chat',
            headerName: this.cvLocalizationService.getCachedText('Quality_Score_Module', 'Chat', 'Chat'),
            filter: 'agTextColumnFilter',
            aggFunc: 'sum',
            cellStyle: {
                textAlign: 'right',
                paddingRight: '22px'
            },
            width: 133,
            minWidth: 133
        },
        {
            field: 'Email',
            headerName: this.cvLocalizationService.getCachedText('Quality_Score_Module', 'Email', 'Email'),
            aggFunc: 'sum',
            cellStyle: {
                textAlign: 'right',
                paddingRight: '22px'
            },
            width: 133,
            minWidth: 133
        },
        {
            field: 'Voice',
            headerName: this.cvLocalizationService.getCachedText('Quality_Score_Module', 'Voice', 'Voice'),
            aggFunc: 'sum',
            cellStyle: {
                textAlign: 'right',
                paddingRight: '22px'
            },
            width: 133,
            minWidth: 133
        }
    ];

    updateGridForEvaluations() {
        this.gridApi.setGridOption('columnDefs', this.evaluationsColumnDefs);
        this.gridApi.setGridOption('rowData', evaluationsData.EvaluationsData);
        this.gridApi.sizeColumnsToFit();
    }

    updateGridForChannels() {
        this.gridApi.setGridOption('columnDefs', this.channelsColumnDefs);
        this.gridApi.setGridOption('rowData', evaluationsData.ChannelsData);
        this.gridApi.sizeColumnsToFit();
    }
    onGridReady(event: GridReadyEvent) {
        this.gridApi = event.api;

        this.gridReady = true;
        this.stateRestoreInProgress = true;

        this.restoreColumnState();
        if (!this.tableState) {
            this.agGridHelperService.sizeColumnsSmart(event);
            this.updateTableState();
        }

        if (this.moduleState.view === this.displayView.Channels) {
            this.updateGridForChannels();
        } else {
            this.updateGridForEvaluations();
        }

        setTimeout(() => this.stateRestoreInProgress = false, 0);
    }

    updateTableState() {
        if (!this.gridApi || this.stateRestoreInProgress)
            return;

        const tableState = this.agGridHelperService.getGridState(this.gridApi);
        if (tableState != null) {
            this.tableState = tableState;
        }
        this.tableStateSave();
    }

    tableStateSave() {
        if (this.stateSave !== null) {
            clearTimeout(this.stateSave);
        }

        this.stateSave = window.setTimeout(async () => {
            this.stateSave = null;
            if (await this.moduleManager.saveState('tableState')) {
                this.moduleManager.requestPublishNeeded();
            }
        }, 500);
    }

    restoreColumnState() {
        if (!this.tableState || !this.gridApi) {
            return;
        }
        this.agGridHelperService.loadGridState(this.gridApi, this.tableState);
    }

    onColumnVisible(event: ColumnVisibleEvent) {
        if (this.tableState == null || this.tableState.columns.map(c => c.width).filter(w => w != null).length === 0)
            this.agGridHelperService.sizeColumnsSmart(event);
        this.updateTableState();
    }

    onColumnResized(event: ColumnResizedEvent) {
        if (event.source == "uiColumnResized" || (event.source == "autosizeColumns" && !this.moduleManager.container.isTogglingFullscreen)) {
            this.updateTableState();
        }
        this.refreshHeaderComponent(event.api);
    }

    onGridSizeChanged(event: GridSizeChangedEvent) {
        if (this.isResizeWaiting) {
            this.agGridHelperService.sizeColumnsSmart(event);
            this.tableStateSave();
        } else if (this.moduleManager.container.isFullscreen) {
            this.agGridHelperService.sizeColumnsSmart(event);
        } else {
            this.restoreColumnState();
        }
    }

    private setupGrid() {
        this.gridOptions = this.agGridHelperService.createGridOptions({
            defaultColDef: {
                sortable: false,
                filter: true,
                lockPinned: true,
                lockVisible: true,
                suppressMovable: true,
                resizable: true,
                icons: this.configService.isCxOne ? {
                    sortAscending: '<div class="sortIcons"><svg class="icon-10 fill-cxoneBlue"><use href="../App_Themes/CXone/icons/arrow-up.svg#arrow-up"></use></svg><svg class="icon-10 fill-sort"><use href="../App_Themes/CXone/icons/arrow-down.svg#arrow-down"></use></svg></div>',
                    sortDescending: '<div class="sortIcons"><svg class="icon-10 fill-sort"><use href="../App_Themes/CXone/icons/arrow-up.svg#arrow-up"></use></svg><svg class="icon-10 fill-cxoneBlue"><use href="../App_Themes/CXone/icons/arrow-down.svg#arrow-down"></use></svg></div>',
                    filter: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/filter.svg#filter"></use></svg>',
                    menu: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/widget-menu.svg#widget-menu"></use></svg>',
                    columns: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/chart.svg#chart"></use></svg>',
                }: null,
            },
            accentedSort: true,
            animateRows: true,
            rowHeight: 44,
            suppressCellFocus: true,
            suppressScrollOnNewData: true,
            suppressContextMenu: false,
            context: { moduleManager: this.moduleManager },
            suppressNoRowsOverlay: true,
            components: {
                agentNameRenderer: this.agGridHelperService.GroupDrillDownCellRenderer,
                groupHeaderComponent: this.agGridHelperService.GroupHeaderComp,
                QualityScoreMetricCellRenderer: QualityScoreMetricCellRendererComponent,
                QualityScoreUserCellRenderer: QualityScoreUserCellRendererComponent,
            },
            onGridReady: () => {
                if (this.data?.Error == ErrorCode.NoError) {
                    if (this.agGrid?.api) {
                        this.agGrid?.api.showLoadingOverlay();
                        this.queueUpdateGrid();
                        this.agGrid?.api.autoSizeAllColumns();
                    }
                }
                this.applyFilter();
            },
            onRowDataUpdated: () => {
                this.agGrid?.api.hideOverlay();
            },
            getRowId: ({ data: rowData }) => rowData.GroupId,
            onGridSizeChanged: event => {
                if (this.isResizeWaiting) {
                    event.api.sizeColumnsToFit();
                    this.queueStateSave();
                } else if (this.moduleManager.container.isFullscreen) {
                    this.agGridHelperService.sizeColumnsSmart(event);
                } else {
                    event.api.sizeColumnsToFit();
                }
            },
            onSortChanged: event => this.queueStateSave(),
            onColumnMoved: event => this.queueStateSave(),
            onColumnPinned: event => this.queueStateSave(),
            onColumnVisible: event => {
                this.agGrid?.api.autoSizeAllColumns(false);
                this.queueStateSave();
            },
            // external filtering (our search bar)
            isExternalFilterPresent: () => !!this.filterValue,
            doesExternalFilterPass: node => {
                return this.agGridHelperService.doesExternalFilterPass(node, this.filterValue);
            },
            onFilterChanged: event => this.queueStateSave(),
            // grid events
            onFirstDataRendered: event => this.agGrid?.api.sizeColumnsToFit(),
        }, {
            enableCopy: true
        });

        // Wire context menus
        this.agContextMenuService.agentDetailsClicked.subscribe( async params => {
            // if multiple widgets are opened that support this option we need to assure only the clicked one opens the modal
            if (params.moduleKey !== this.moduleManager.getModuleKey())
                return;
            const response = await this.agentStateService.loadCurrentAgentStatesByGroupID(params.moduleKey, params.groupData.groupID);
            this.openAgentStateDetail(params.moduleKey, params.groupData.groupID, response);
        });

        this.agContextMenuService.createTaskClicked.subscribe( async params => {
            // if multiple widgets are opened that support this option we need to assure only the clicked one opens the modal
            if (params.moduleKey !== this.moduleManager.getModuleKey()) {
                return;
            }
            this.openCreateTaskModal(params.moduleKey, params.groupData.groupID, params.groupData);
        });

        this.agContextMenuService.suggestCoachingClicked.subscribe(async params => {
            if (params.moduleKey !== this.moduleManager.getModuleKey()) {
                return;
            }
            this.openSuggestCoachingModal(params.moduleKey, params.groupData.groupID, params.groupData);
        })
    }

    /** Opens the Agent State Detail modal from the Context Menu on Groups column */
    openAgentStateDetail(moduleKey: string, groupID: number, selectedRow: any ) {
        this.navigationService.showOverlay = true;
        this.modalRef = this.ngbModal.open(AgentStateDetailComponent,
            {size : "xl", centered: true, modalDialogClass: this.configService.isCxOne ? 'cx-one-ui' : null});
        this.modalRef.result.finally(() => this.navigationService.showOverlay = false);
        const component: AgentStateDetailComponent = this.modalRef.componentInstance;
        component.moduleKey = moduleKey;
        component.groupID = groupID;
        component.selectedRow = selectedRow;
        component.dashKey = this.moduleManager.container.dashboardManager.dashboardKey;
    }

    openCreateTaskModal(moduleKey: string, groupID: number, selectedRow: any) {
        this.navigationService.showOverlay = true;
        this.modalRef = this.ngbModal.open(TasksModalComponent,
            {size : "xl", centered: true, scrollable: true, modalDialogClass: null});
        this.modalRef.result.finally(() => this.navigationService.showOverlay = false);
        const component: TasksModalComponent = this.modalRef.componentInstance;
        component.moduleKey = moduleKey;
        component.groupID = groupID;
        component.selectedRow = selectedRow;
        component.dashKey = this.moduleManager.container.dashboardManager.dashboardKey;   
    }

    openSuggestCoachingModal(moduleKey: string, groupID: number, selectedRow: any) {
        this.navigationService.showOverlay = true;
        this.modalRef = this.ngbModal.open(SuggestCoachingModalComponent,
            { size: "md", centered: true, scrollable: true, modalDialogClass: null });
        this.modalRef.result.finally(() => this.navigationService.showOverlay = false);
        const component: SuggestCoachingModalComponent = this.modalRef.componentInstance;
        component.moduleKey = moduleKey;
        component.groupID = groupID;
        component.selectedRow = selectedRow;
        component.dashKey = this.moduleManager.container.dashboardManager.dashboardKey;
    }

    public applyFilter() {
        if (this.agGrid?.api) {
            this.filterValue = this.filterValue.trim();
            this.agGrid?.api.setGridOption('quickFilterText', this.filterValue);
        }
    }

    private queueStateSave() {
        if (this.stateSaveTimer) {
            clearTimeout(this.stateSaveTimer);
            this.stateSaveTimer = null;
        }

        this.stateSaveTimer = window.setTimeout(() => {
            this.stateSaveTimer = null;
            if (this.agGrid?.api)
                this.gridState = this.agGridHelperService.getGridState(this.agGrid?.api);
            this.moduleManager.saveState(); // This will save all moduleManager.addState definitions
        }, 500/*ms*/);
    };

    public getScoresForAutoView() {
        //this will be changing later but for now using this to hide "N/A" data on Top/Bottom tab
        this.topScores = [];
        this.bottomScores = [];

        const nonNaScores = this.data.RowData.filter(d => !d.IsNa)?.reverse();
        for (let i = 0; i < 3 && nonNaScores.length > 0; i++) {
            this.topScores.push(nonNaScores.pop());
        }
        nonNaScores?.reverse();
        for(let i = 0; i < 3 && nonNaScores.length > 0; i++) {
            this.bottomScores.push(nonNaScores.pop());
        }
        this.bottomScores?.reverse();
    }

    public onChange(event) {
        if (event.id == 'TopBottom')
            this.isCustomView = false;
        else
            this.isCustomView = true;
    }

    public async selectedGroupsChanged(e: GroupSelectorDTO) {
        this.viewBy = this.moduleManager?.customSettings?.SelectedView?.getValue();
        if (this.isFirstLoad) {
            return;
        }
        if (e == this.selectedAgentsDTO)
            return;

        await this.loadData(false);
        if (this.data.RowData.length === 0) {
            this.noData = true;
            return;
        }

        this.selectedAgentsDTO = e;
        await this.moduleManager.loadSettings(false);

        if(this.moduleManager.container.dashboardManager.writeAccess)
            this.moduleManager.requestPublishNeeded();
    }

    public async initialGroupsLoaded(e: GroupSelectorDTO) {
        if (!this.isFirstLoad) {
            return;
        }
        if (this.data.RowData.length === 0) {
            this.isFirstLoad = false;
            this.noData = true;
            return;
        }

        this.selectedAgentsDTO = e;
        await this.moduleManager.loadSettings(false);
        this.isFirstLoad = false;
    }

    public formatValue(value: number) {
        const val = Math.round(value * 1e1) / 1e1;
        return val > 100 ? 100 : val;
    }
        
    public getLocaleText(key: string, defaultValue?: string) {
        return this.cvLocalizationService.getCachedText("Quality_Score_Module", key, defaultValue);
    }

    public formatNumber(value: string | number): string | number {
        let parsedValue = typeof value === 'string' ? parseFloat(value): value;
        if (Number.isNaN(parsedValue)) {
          return value;
        }

        if (Number.isInteger(parsedValue)) {
            return parsedValue;
        } else {
            const factor = Math.pow(10, 2);
            return  Math.round(parsedValue * factor) / factor;
        }
    }
}
